import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Amplify } from "aws-amplify";
import { Authenticator, Theme, ThemeProvider, View, useTheme } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import { ChakraProvider, theme, Text, Image } from "@chakra-ui/react";
import { ChakraProvider as ChakraProviderV3, defaultSystem } from "@chakra-ui/react-v3";
import { createQueryClient, QueryClientProvider } from "@metriport/shared-internal";
import { AppStateProvider } from "./components/contexts/app";
import * as Sentry from "@sentry/react";
import Home from "./components/home/HomePage";
import CustomerPage from "./components/customer/CustomerPage";
import FacilitiesPage from "./components/customer/facility/FacilitiesPage";
import BulkPatientImportPage from "./components/customer/facility/BulkPatientImportPage";
import FeatureFlagManagementPage from "./components/feature-flags/FeatureFlagManagementPage";
import ToolsPage from "./components/tools/ToolsPage";
import BucketsPage from "./components/buckets/BucketsPage";
import EhrDetailPage from "./components/ehr/EhrDetailPage";
import EhrListPage from "./components/ehr/EhrListPage";
import EhrSearchPage from "./components/ehr/EhrSearchPage";
import Navbar from "./components/navbar/Navbar";
import "./index.css";

const queryClient = createQueryClient();

Amplify.configure({
  Auth: {
    userPoolId: process.env.REACT_APP_USER_POOL_ID,
    region: process.env.REACT_APP_AWS_REGION,
    userPoolWebClientId: process.env.REACT_APP_USER_POOL_WEB_CLIENT_ID,
  },
});

const logo = "logo.png";

const amplifyTheme: Theme = {
  name: "custom-theme",
  tokens: {
    colors: {
      font: {
        primary: { value: "black" },
        secondary: { value: "#879ced" },
      },
      brand: {
        primary: { "80": { value: "#748df0" } },
      },
    },
  },
};

const components = {
  Header() {
    const { tokens } = useTheme();

    return (
      <View textAlign="center" padding={tokens.space.large}>
        <Image marginTop={"40%"} alt="Metriport logo" width={"70%"} src={logo} />
        <Text>Operations Portal</Text>
      </View>
    );
  },
  Footer() {
    const { tokens } = useTheme();

    return (
      <View textAlign="center" padding={tokens.space.large}>
        <Text>&copy; All Rights Reserved</Text>
      </View>
    );
  },
};

// https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/#usage-with-react-router-64-data-api
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

export const App = () => {
  return (
    <ThemeProvider theme={amplifyTheme}>
      <Authenticator components={components} loginMechanisms={["email"]} hideSignUp>
        {({ signOut, user }) => (
          <main>
            <BrowserRouter>
              <ChakraProvider theme={theme}>
                <ChakraProviderV3 value={defaultSystem}>
                  <QueryClientProvider client={queryClient}>
                    <AppStateProvider user={user}>
                      <Navbar
                        children={
                          <SentryRoutes>
                            <Route path="/" element={<Home />} />
                            <Route path="/feature-flags" element={<FeatureFlagManagementPage />} />
                            <Route path="/tools" element={<ToolsPage />} />
                            <Route path="/buckets" element={<BucketsPage />} />
                            <Route path="/buckets/:bucketName/*" element={<BucketsPage />} />
                            <Route path="/ehr/:cxId/:ehrName" element={<EhrDetailPage />} />
                            <Route path="/ehr/:cxId" element={<EhrListPage />} />
                            <Route path="/ehr" element={<EhrSearchPage />} />
                            <Route path="/customer/:cxId" element={<CustomerPage />} />
                            <Route path="/customer/:cxId/facilities" element={<FacilitiesPage />} />
                            <Route
                              path="/customer/:cxId/facilities/:facilityId"
                              element={<FacilitiesPage />}
                            />
                            <Route
                              path="/customer/:cxId/facilities/:facilityId/bulk-import"
                              element={<BulkPatientImportPage />}
                            />
                          </SentryRoutes>
                        }
                        signOut={signOut}
                        user={user}
                      />
                    </AppStateProvider>
                  </QueryClientProvider>
                </ChakraProviderV3>
              </ChakraProvider>
            </BrowserRouter>
          </main>
        )}
      </Authenticator>
    </ThemeProvider>
  );
};
